import type { DraftState, WizardQuestion, WizardSampleGroup } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { IUPAC_PRIMER } from '../../Util/orderSettingDetection/iupac.ts'
import { primersFor } from './sheet/applyPlan.ts'
import { isArchiveOrder } from './archive.ts'
import { writtenEstimate } from './primerOrientation.ts'
import { placedGroups } from './pools.ts'
import { itemsByType } from './sampleLabels.ts'

const TRIMMED = ['TRIMMED_SAMPLES', 'MERGED_AND_TRIMMED_SAMPLES']

export const isUntrimmed = (draft: DraftState): boolean => {
  const value = draft.settings?.sampleConfiguration.value
  return !!value && !TRIMMED.includes(value)
}

/** Whether the item gets both primers: set in the order, or written by the wizard (sheet, entered, or estimated). */
export const hasPrimers = (draft: DraftState, group: WizardSampleGroup): boolean => {
  const primers = primersFor(draft, group)
  return !!group.primersOnServer || (!!primers.forward && !!primers.reverse)
}

/**
 * Items that would reach submit without both primers, which the classic check refuses: every item of an
 * untrimmed order; in a trimmed order, every item once any has primers. Archives are checked with their
 * sheet samples.
 */
export const groupsWithoutPrimers = (draft: DraftState): WizardSampleGroup[] => {
  if (isArchiveOrder(draft) || !draft.settings) {
    return []
  }
  const groups = placedGroups(draft)
  const missing = groups.filter((group) => !hasPrimers(draft, group))
  return isUntrimmed(draft) || missing.length < groups.length ? missing : []
}

export type FillPair = { forward: string; reverse: string; kind: 'shared' | 'gene' }

const mostCommon = (values: string[]): string | undefined => {
  const counts = new Map<string, number>()
  values.forEach((value) => counts.set(value, (counts.get(value) ?? 0) + 1))
  return [...counts].sort((a, b) => b[1] - a[1])[0]?.[0]
}

/**
 * Primers to fill the gaps with: the pair every item with primers shares; where tags make them differ,
 * the gene primers without a tag (as the lab spells them in the estimates, else the registry's spelling).
 */
export const fillPair = (draft: DraftState): FillPair | undefined => {
  const pairs = placedGroups(draft)
    .map((group) => primersFor(draft, group))
    .filter((primers) => !!primers.forward && !!primers.reverse)
  const distinct = [...new Set(pairs.map((primers) => `${primers.forward}\u0000${primers.reverse}`))]
  if (distinct.length === 1) {
    const [forward, reverse] = distinct[0].split('\u0000')
    return { forward, reverse, kind: 'shared' }
  }
  const estimates = placedGroups(draft).flatMap((group) => writtenEstimate(draft, group) ?? [])
  const pair = draft.discovery?.primers.status === 'found' ? draft.discovery.primers.pair : undefined
  const forward =
    mostCommon(estimates.flatMap((estimate) => (estimate.forward.gene ? [estimate.forward.gene] : []))) ?? pair?.firstVariants[0]
  const reverse =
    mostCommon(estimates.flatMap((estimate) => (estimate.reverse.gene ? [estimate.reverse.gene] : []))) ?? pair?.secondVariants[0]
  return forward && reverse ? { forward, reverse, kind: 'gene' } : undefined
}

/** Whether every missing item's estimate is in (or cannot come), so asking is not premature. */
export const primerGapSettled = (draft: DraftState, missing: readonly WizardSampleGroup[]): boolean =>
  draft.discovery?.primers.status !== 'found' || !!draft.restored || missing.every((group) => group.estimate !== undefined)

type PrimersMissing = Pick<Extract<WizardQuestion, { kind: 'primers-missing' }>, 'groupKeys' | 'controls' | 'suggestion' | 'sheetUsed'>

/** Who lacks primers: "2 negative controls", or "3 items (1 negative control)". */
export const primersMissingSubject = ({ groupKeys, controls }: PrimersMissing): string => itemsByType(groupKeys.length - controls, controls)

/** Which way of filling the primers leads, when no shared pair does: the sheet once one was used, else typing them. */
export const primersMissingLeads = ({ suggestion, sheetUsed }: PrimersMissing): { sheet: boolean; entry: boolean } => {
  const open = !suggestion || suggestion.kind === 'gene'
  return { sheet: open && sheetUsed, entry: open && !sheetUsed }
}

/** Typed primers worth taking: both IUPAC, and for a tagged design not just the gene primers offered without a tag. */
export const enteredPrimersUsable = (forward: string, reverse: string, suggestion: PrimersMissing['suggestion']): boolean => {
  const [f, r] = [forward.trim().toUpperCase(), reverse.trim().toUpperCase()]
  const untouched = suggestion?.kind === 'gene' && f === suggestion.forward.toUpperCase() && r === suggestion.reverse.toUpperCase()
  return IUPAC_PRIMER.test(f) && IUPAC_PRIMER.test(r) && !untouched
}
